import { Link } from 'react-router-dom'
import { RiCheckLine, RiCalendarCheckLine, RiSparklingLine } from 'react-icons/ri'
import SEO from '../../components/seo/SEO'
import { breadcrumb } from '../../components/seo/jsonLd'

const TIERS = [
  {
    name: 'Essential',
    tagline: 'For everyday upkeep',
    priceFrom: 800,
    accent: 'from-salon-accent/20 to-white',
    border: 'border-salon-cream-dark',
    features: [
      'Classic haircut & blow-dry',
      'Basic facial cleanup',
      'Express manicure',
      'Eyebrow threading',
      'Hot oil scalp massage (15 min)',
    ],
  },
  {
    name: 'Signature',
    tagline: 'Our most-loved package',
    priceFrom: 3500,
    accent: 'from-salon-primary to-salon-primary-dark',
    border: 'border-salon-primary',
    highlighted: true,
    features: [
      'Premium haircut & styling',
      'Hair colour or highlights',
      'Glow facial (60 min)',
      'Mani-pedi with gel finish',
      'Head, neck & shoulder massage',
      'Complimentary hair spa',
    ],
  },
  {
    name: 'Bridal Couture',
    tagline: 'Your big day, perfected',
    priceFrom: 18000,
    accent: 'from-salon-secondary to-salon-secondary-light',
    border: 'border-salon-secondary',
    features: [
      'Pre-bridal skin & hair plan (4 sessions)',
      'HD bridal makeup',
      'Bridal hairstyling & extensions',
      'Saree / lehenga draping',
      'Mehendi coordination',
      'On-location stylist team',
      'Trial session included',
    ],
  },
]

const A_LA_CARTE = [
  { name: 'Haircut & blow-dry',         price: '₹800 – ₹1,800' },
  { name: 'Global hair colour',          price: '₹3,500 – ₹6,500' },
  { name: 'Balayage / ombré',            price: '₹6,500 – ₹12,000' },
  { name: 'Keratin / smoothening',       price: '₹6,500 – ₹15,000' },
  { name: 'Bridal makeup (HD/airbrush)', price: '₹15,000 – ₹35,000' },
  { name: 'Party / event makeup',        price: '₹3,500 – ₹6,500' },
  { name: 'Classic facial (60 min)',     price: '₹1,200 – ₹2,200' },
  { name: 'Advanced facial (90 min)',    price: '₹2,800 – ₹4,500' },
  { name: 'Manicure',                    price: '₹600 – ₹1,200' },
  { name: 'Pedicure',                    price: '₹800 – ₹1,500' },
  { name: 'Gel / nail extensions',       price: '₹1,500 – ₹3,500' },
  { name: 'Head spa & scalp therapy',    price: '₹1,500 – ₹3,000' },
]

export default function Pricing() {
  return (
    <>
      <SEO
        title="Pricing — Transparent Salon Rates in Chennai"
        description="See Salon Vora's transparent rates for haircuts, colour, balayage, bridal makeup, facials, manicures, pedicures and wellness. No hidden charges."
        path="/pricing"
        jsonLd={breadcrumb([
          { name: 'Home', url: '/' },
          { name: 'Pricing', url: '/pricing' },
        ])}
      />

      <div className="min-h-screen bg-salon-cream">
        {/* Hero */}
        <section className="relative bg-hero-gradient py-32 overflow-hidden">
          <div className="absolute inset-0 opacity-20"
            style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #C9963A 0%, transparent 60%)' }} />
          <div className="container-salon relative z-10 text-center">
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-salon-secondary px-3 py-1 rounded-full bg-salon-secondary/10 mb-3">
              Pricing
            </span>
            <h1 className="font-display text-4xl md:text-6xl text-white font-bold mb-4">
              Honest Rates. <span className="text-salon-secondary italic">No Surprises.</span>
            </h1>
            <p className="text-white/70 font-body max-w-2xl mx-auto">
              Pick a curated package or build your own à la carte. Final quotes are confirmed at consultation based on hair length, condition and add-ons.
            </p>
          </div>
        </section>

        {/* Tiers */}
        <section className="py-20 container-salon">
          <div className="grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {TIERS.map((t) => (
              <div key={t.name}
                className={`relative rounded-3xl overflow-hidden border-2 ${t.border} ${
                  t.highlighted ? 'shadow-salon-lg lg:-translate-y-4 text-white' : 'bg-white shadow-salon-sm'
                }`}>
                <div className={`p-8 ${t.highlighted ? `bg-gradient-to-br ${t.accent}` : ''}`}>
                  {t.highlighted && (
                    <span className="absolute top-4 right-4 inline-flex items-center gap-1 bg-salon-secondary text-salon-dark
                                     text-xs font-bold px-3 py-1 rounded-full">
                      <RiSparklingLine /> Most Loved
                    </span>
                  )}
                  <p className={`text-sm font-body mb-1 ${t.highlighted ? 'text-white/70' : 'text-salon-muted'}`}>{t.tagline}</p>
                  <h3 className={`font-display text-3xl font-bold mb-4 ${t.highlighted ? 'text-white' : 'text-salon-dark'}`}>{t.name}</h3>
                  <p className={`font-body mb-6 ${t.highlighted ? 'text-white' : 'text-salon-dark'}`}>
                    <span className={`text-xs uppercase tracking-widest ${t.highlighted ? 'text-white/60' : 'text-salon-muted'}`}>From</span>
                    <span className="font-display font-bold text-4xl ml-2">₹{t.priceFrom.toLocaleString()}</span>
                  </p>
                  <ul className="space-y-2.5 mb-8">
                    {t.features.map((f) => (
                      <li key={f} className={`flex items-start gap-2 text-sm font-body ${t.highlighted ? 'text-white/90' : 'text-salon-dark'}`}>
                        <RiCheckLine className={t.highlighted ? 'text-salon-secondary mt-0.5 shrink-0' : 'text-salon-primary mt-0.5 shrink-0'} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link to="/book" className={t.highlighted
                      ? 'btn-gold w-full justify-center'
                      : 'btn-secondary w-full justify-center'}>
                    <RiCalendarCheckLine /> Book {t.name}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* À la carte */}
        <section className="py-20 bg-white">
          <div className="container-salon">
            <div className="text-center mb-12">
              <span className="section-tag">À la carte menu</span>
              <h2 className="section-title mt-2">Service-by-Service Rates</h2>
              <p className="text-salon-muted font-body mt-3 max-w-xl mx-auto">
                Indicative starting prices in INR. Final quote depends on stylist seniority, length and product brand.
              </p>
            </div>
            <div className="max-w-3xl mx-auto rounded-2xl border border-salon-cream-dark overflow-hidden bg-white shadow-salon-sm">
              {A_LA_CARTE.map((row, i) => (
                <div key={row.name}
                  className={`flex justify-between items-center px-6 py-4 ${i % 2 ? 'bg-salon-cream/40' : ''}`}>
                  <span className="font-body text-salon-dark text-sm sm:text-base">{row.name}</span>
                  <span className="font-body font-semibold text-salon-primary text-sm sm:text-base">{row.price}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-salon-muted font-body text-sm mt-6">
              Membership and student discounts available — <Link to="/contact" className="text-salon-primary font-semibold hover:underline">ask us</Link>.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-salon-cream">
          <div className="container-salon">
            <div className="bg-salon-primary rounded-3xl px-8 py-12 text-center">
              <h2 className="font-display text-white text-3xl md:text-4xl font-bold mb-3">Want a personalised quote?</h2>
              <p className="text-white/80 font-body mb-8 max-w-lg mx-auto">
                Tell us a bit about what you're looking for and we'll send a tailored package the same day.
              </p>
              <Link to="/contact" className="btn-gold text-base px-10 py-4">Request a Quote</Link>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}
